/**
 * API da Central de Projetos (ADR-0089).
 *
 * Todas as funções recebem o `request` do AuthContext (refresh de token,
 * passo extra de MFA quando a permissão pede, erro com `payload.detalhe`).
 * As leituras de fundo vão com `silentError`; as ações deixam o erro chegar ao
 * AsyncButton, que mostra o toast.
 */

const P = "/api/central-projetos";

function desembrulhar(resposta) {
  return resposta?.data ?? resposta;
}

function query(params = {}) {
  const busca = new URLSearchParams();
  Object.entries(params).forEach(([chave, valor]) => {
    if (valor !== undefined && valor !== null && valor !== "") busca.set(chave, String(valor));
  });
  const texto = busca.toString();
  return texto ? `?${texto}` : "";
}

function enviar(request, url, metodo, corpo, opcoes = {}) {
  return request(url, { method: metodo, body: corpo === undefined ? undefined : JSON.stringify(corpo), ...opcoes })
    .then(desembrulhar);
}

export const lerEstado = (request) => request(`${P}/estado`, { silentError: true }).then(desembrulhar);
export const lerPainel = (request) => request(`${P}/painel`, { silentError: true }).then(desembrulhar);
/** Por pedido: quem espera, urgência, pendências, projetistas (lista de Clientes do Legado). */
export const lerCarteira = (request) => request(`${P}/carteira`, { silentError: true }).then(desembrulhar);
export const lerFicha = (request, pedido) =>
  request(`${P}/clientes/${encodeURIComponent(pedido)}`, { silentError: true }).then(desembrulhar);
export const pedirAnalise = (request, pedido) =>
  enviar(request, `${P}/clientes/${encodeURIComponent(pedido)}/analisar`, "POST", {});
/** Corrige o papel de quem fala nas conversas do cliente; vale sobre a regra e a IA. */
export const corrigirPapelDoParticipante = (request, pedido, contatoId, papel) =>
  enviar(
    request,
    `${P}/clientes/${encodeURIComponent(pedido)}/participantes/${encodeURIComponent(contatoId)}`,
    "PATCH",
    { papel },
  );
/** Pede ao Gemini o texto do perfil agora (gasta 1 pedido da reserva do dia). O erro a tela mostra inline. */
export const escreverPerfil = (request, pedido) =>
  enviar(request, `${P}/clientes/${encodeURIComponent(pedido)}/perfil/escrever`, "POST", {}, { silentError: true });
export const adicionarApelido = (request, pedido, apelido) =>
  enviar(request, `${P}/clientes/${encodeURIComponent(pedido)}/apelidos`, "POST", { apelido });
export const removerApelido = (request, pedido, id) =>
  enviar(request, `${P}/clientes/${encodeURIComponent(pedido)}/apelidos${query({ id })}`, "DELETE");

/** URL nova da miniatura da imagem (a de 15 min venceu): `{miniatura, largura, altura}`. */
export const renovarMiniatura = (request, mensagemId) =>
  request(`${P}/mensagens/${mensagemId}/imagem`, { silentError: true }).then(desembrulhar);
/** Caminho da imagem grande pela API (lida com `buscarArquivoComAuth`: o bucket não tem CORS). */
export const arquivoDaImagem = (mensagemId) => `${P}/mensagens/${mensagemId}/imagem/arquivo`;

/** "Não é de Projetos": tira o selo da mensagem (auditado). Devolve `{mensagem: {id, projetos}, conversa}`. */
export const tirarSeloProjetos = (request, mensagemId) =>
  enviar(request, `${P}/mensagens/${mensagemId}/nao-e-projetos`, "POST", {});

/** Mensagens (texto mascarado; leitura auditada, sem MFA desde 04/11/2026). O erro a tela mostra. */
export const lerMensagens = (request, params) =>
  request(`${P}/mensagens${query(params)}`, { silentError: true }).then(desembrulhar);

export const listarPendencias = (request, filtros) =>
  request(`${P}/pendencias${query(filtros)}`, { silentError: true }).then(desembrulhar);
export const criarPendencia = (request, corpo) => enviar(request, `${P}/pendencias`, "POST", corpo);
export const agirPendencia = (request, id, acao, corpo) =>
  enviar(request, `${P}/pendencias/${id}/${acao}`, "POST", corpo);
export const agirEmLote = (request, corpo) => enviar(request, `${P}/pendencias/lote`, "POST", corpo);
export const listarResponsaveis = (request) =>
  request(`${P}/responsaveis`, { silentError: true }).then(desembrulhar);

export const listarConversas = (request, filtros) =>
  request(`${P}/conversas${query(filtros)}`, { silentError: true }).then(desembrulhar);
/** `{classificacao}` ou `{numero_pedido}` ("" desfaz o vínculo); devolve o item da conversa. */
export const atualizarConversa = (request, id, corpo) => enviar(request, `${P}/conversas/${id}`, "PATCH", corpo);
/**
 * Refaz a prévia agora pelo Gemini (gasta 1 pedido da fatia de prévias do dia).
 * Ao abrir a conversa a tela usa a prévia da última rodada, sem chamar nada.
 * Devolve o item da conversa; o erro (`detalhe`) a tela mostra inline.
 */
export const atualizarPrevia = (request, id) =>
  enviar(request, `${P}/conversas/${id}/previa`, "POST", { atualizar: true }, { silentError: true });
/** "Tratado" no alerta Projetos (auditado). Devolve o item da conversa, se o servidor mandar. */
export const marcarAlertaTratado = (request, id) =>
  enviar(request, `${P}/conversas/${id}/alerta-tratado`, "POST", {});
/**
 * Envia pelo WhatsApp da marcenaria (fila de comandos que o monitor executa).
 * `{tipo: "texto"|"arquivo"|"audio", texto, arquivo, citadaId, marcarLida}` vira o
 * multipart de `POST /conversas/{id}/enviar`; devolve o comando criado. O erro
 * (429 com `detalhe`, por exemplo) a tela mostra no próprio balão.
 */
export function enviarMensagem(request, conversaId, { tipo, texto, arquivo, citadaId, marcarLida = false }) {
  const corpo = new FormData();
  corpo.append("tipo", tipo);
  if (texto) corpo.append("texto", texto);
  if (arquivo) corpo.append("arquivo", arquivo, arquivo.name);
  if (citadaId) corpo.append("citada_id", citadaId);
  corpo.append("marcar_lida", marcarLida ? "1" : "0");
  return request(`${P}/conversas/${conversaId}/enviar`, { method: "POST", body: corpo, silentError: true })
    .then(desembrulhar)
    .then((resposta) => resposta?.comando);
}
/** Pede ao monitor que marque a conversa como lida no WhatsApp; devolve o comando. */
export const marcarConversaComoLida = (request, conversaId) =>
  enviar(request, `${P}/conversas/${conversaId}/marcar-lida`, "POST", {}).then((resposta) => resposta?.comando);
/** Comandos ainda sem a mensagem de volta (pendente, enviando ou falhou, últimas 24 h). */
export const listarComandosAbertos = (request, conversaId) =>
  request(`${P}/conversas/${conversaId}/comandos${query({ abertos: 1 })}`, { silentError: true })
    .then(desembrulhar)
    .then((resposta) => resposta?.comandos || []);
/** Comando que falhou volta para a fila (a tela relê os comandos depois). */
export const repetirComando = (request, id) =>
  enviar(request, `${P}/comandos/${id}/repetir`, "POST", {}, { silentError: true });
/** Pedidos para ligar uma conversa: número, cliente e etapa. */
export const buscarClientes = (request, q) =>
  request(`${P}/clientes-busca${query({ q })}`, { silentError: true }).then(desembrulhar);

/**
 * `{pergunta, conversa_id?, citada_id?}`. Sem `conversa_id` o servidor cria a
 * conversa; a resposta traz `conversa` (item da lista) e os dois `turnos`
 * gravados. Falha não grava nada.
 */
export const perguntarAoAssistente = (request, corpo) =>
  enviar(request, `${P}/assistente`, "POST", corpo, { silentError: true });
/** Conversas salvas do Assistente: só as de quem pergunta, as mais novas primeiro. */
export const listarConversasDoAssistente = (request) =>
  request(`${P}/assistente/conversas`, { silentError: true })
    .then(desembrulhar)
    .then((resposta) => resposta?.conversas || []);
export const lerConversaDoAssistente = (request, id) =>
  request(`${P}/assistente/conversas/${id}`, { silentError: true }).then(desembrulhar);
export const renomearConversaDoAssistente = (request, id, titulo) =>
  enviar(request, `${P}/assistente/conversas/${id}`, "PATCH", { titulo });
export const apagarConversaDoAssistente = (request, id) =>
  request(`${P}/assistente/conversas/${id}`, { method: "DELETE" });

export const lerConfiguracao = (request) => request(`${P}/configuracao`).then(desembrulhar);
export const salvarConfiguracao = (request, corpo) => enviar(request, `${P}/configuracao`, "PUT", corpo);
export const gerarTokenMonitor = (request) => enviar(request, `${P}/configuracao/token-monitor`, "POST", {});
/** Custo do mês por modelo e `gemini_hoje` (cota gratuita do dia, por fatia). Exige `configurar`. */
export const lerUsoIA = (request) => request(`${P}/uso-ia`, { silentError: true }).then(desembrulhar);
export const listarConexoesMcp = (request) =>
  request(`${P}/mcp/conexoes`, { silentError: true }).then(desembrulhar);
export const revogarConexaoMcp = (request, id) => enviar(request, `${P}/mcp/conexoes`, "POST", { id });
